import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import html2canvas from 'html2canvas';
import { Share } from 'lucide-react';
import QRCode from 'qrcode';
import { useConfirmModal } from './ConfirmModal';
import { getMarkerCode } from '../utils/color';

const EXPORT_WIDTH = 1080;
const EXPORT_HEIGHT = 1920;
const EXPORT_COLUMNS = 3;
const EXPORT_GAP = 36;
const EXPORT_PADDING_X = 96;
const EXPORT_PADDING_TOP = 40;
const EXPORT_PADDING_BOTTOM = 60;
const EXPORT_HEADER_HEIGHT = 240;

const getPrimaryCode = (color = {}, ohuhuMode = 'both', fallback = '') =>
  getMarkerCode(color, ohuhuMode, fallback);

const ExportSwatch = ({ color, ohuhuMode, cardWidth, cardHeight }) => {
  const brand = typeof color?.brand === 'string' ? color.brand : '';
  const name = typeof color?.name === 'string' ? color.name : '';
  const hex = typeof color?.hex === 'string' && color.hex.trim() !== '' ? color.hex.toUpperCase() : '';
  const fallbackCode = hex || '—';
  const code = getPrimaryCode(color, ohuhuMode, fallbackCode) || fallbackCode;
  const safeHex = hex || '#E5E7EB';

  return (
    <div
      style={{
        width: cardWidth,
        height: cardHeight,
        borderRadius: 28,
        background: safeHex,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 26px 60px -28px rgba(15,23,42,0.35)',
        border: '1px solid rgba(148,163,184,0.12)',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(15,23,42,0.04) 0%, rgba(15,23,42,0.18) 100%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          background: 'rgba(255,255,255,0.9)',
          borderRadius: 20,
          padding: '16px 18px',
          margin: 20,
          width: 'calc(100% - 40px)',
          boxShadow: '0 20px 48px -32px rgba(15,23,42,0.42)',
        }}
      >
        {brand && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              color: '#475569',
              marginBottom: 3,
            }}
          >
            {brand}
          </div>
        )}
        {name && (
          <div
            style={{
              fontSize: 21,
              fontWeight: 500,
              color: '#0f172a',
              letterSpacing: '-0.01em',
              marginBottom: 6,
            }}
          >
            {name}
          </div>
        )}
        <div
          style={{
            fontFamily: "'DM Mono', 'SFMono-Regular', 'Menlo', monospace",
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '0.18em',
            color: '#1f2937',
            textTransform: 'uppercase',
          }}
        >
          {code}
        </div>
      </div>
    </div>
  );
};

export default function SharePaletteModal({ isOpen, onClose, code, palette = [], ohuhuMode = 'both' }) {
  const { t, currentLanguage } = useI18n();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();
  const overlayRef = useRef(null);
  const imageRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [copyType, setCopyType] = useState('code'); // 'code' or 'url'
  const [generatingImage, setGeneratingImage] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const rows = Math.max(1, Math.ceil((palette?.length || 0) / EXPORT_COLUMNS));
  const gridHeightAvailable = Math.max(
    0,
    EXPORT_HEIGHT - (EXPORT_PADDING_TOP + EXPORT_PADDING_BOTTOM + EXPORT_HEADER_HEIGHT)
  );
  const totalRowGaps = EXPORT_GAP * (rows - 1);
  const rawCardHeight = Math.floor((gridHeightAvailable - totalRowGaps) / rows);
  const cardHeight = Math.max(180, Math.min(420, rawCardHeight));
  const gridWidth = EXPORT_WIDTH - (EXPORT_PADDING_X * 2);
  const totalColumnGaps = EXPORT_GAP * (EXPORT_COLUMNS - 1);
  const cardWidth = Math.floor((gridWidth - totalColumnGaps) / EXPORT_COLUMNS);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const onOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose?.();
    }
  };

  const generateShareUrl = () => {
    const url = new URL(window.location.origin + window.location.pathname);
    if (code) {
      url.searchParams.set('code', code);
    }
    return url.toString();
  };

  useEffect(() => {
    let isMounted = true;

    const createQr = async () => {
      if (!isOpen || !code) {
        if (isMounted) {
          setQrDataUrl(null);
        }
        return;
      }
      try {
        const shareUrl = generateShareUrl();
        const dataUrl = await QRCode.toDataURL(shareUrl, {
          margin: 1,
          scale: 6,
          color: { dark: '#0f172a', light: '#ffffffff' },
        });
        if (isMounted) {
          setQrDataUrl(dataUrl);
        }
      } catch (qrError) {
        console.warn('SharePaletteModal: failed to generate QR code', qrError);
        if (isMounted) {
          setQrDataUrl(null);
        }
      }
    };

    createQr();

    return () => {
      isMounted = false;
    };
  }, [isOpen, code]);

  const fallbackCopyText = (text) => {
    let textarea = null;
    const selection = document.getSelection();
    const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    try {
      textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      textarea.style.fontSize = '16px';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      return document.execCommand('copy');
    } catch (fallbackError) {
      console.error('SharePaletteModal: fallback copy failed', fallbackError);
      return false;
    } finally {
      if (selection && typeof selection.removeAllRanges === 'function') {
        selection.removeAllRanges();
        if (selectedRange) {
          selection.addRange(selectedRange);
        }
      }
      if (textarea && textarea.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
    }
  };

  const copyToClipboard = async (type = 'code') => {
    const rawText = type === 'url' ? generateShareUrl() : code || '';
    const textToCopy = typeof rawText === 'string' ? rawText : String(rawText ?? '');
    if (!textToCopy) {
      return;
    }

    let copiedSuccessfully = false;

    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        copiedSuccessfully = true;
      } catch (clipboardError) {
        console.warn('SharePaletteModal: navigator.clipboard.writeText failed', clipboardError);
      }
    }

    if (!copiedSuccessfully) {
      copiedSuccessfully = fallbackCopyText(textToCopy);
    }

    if (copiedSuccessfully) {
      setCopyType(type);
      setCopied(true);
    } else {
      console.error('SharePaletteModal: failed to copy text to clipboard');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setCopyType('code');
      setGeneratingImage(false);
      setQrDataUrl(null);
    }
  }, [isOpen, code]);

  const handleDownloadImage = async () => {
    if (!palette || palette.length === 0) {
      await showConfirm({
        title: t('sharePaletteModal.error'),
        message: t('sharePaletteModal.noPaletteError'),
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
      return;
    }

    if (!imageRef.current) {
      console.warn('SharePaletteModal: image reference missing');
      return;
    }

    setGeneratingImage(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const deviceScale = window.devicePixelRatio && window.devicePixelRatio > 1 ? window.devicePixelRatio : 1;
      const scale = Math.min(2, Math.max(1.5, deviceScale));
      const qrImage = imageRef.current.querySelector('img[data-qr="true"]');
      if (qrImage && !qrImage.complete) {
        await new Promise((resolve) => {
          qrImage.onload = resolve;
          qrImage.onerror = resolve;
        });
      }
      const canvas = await html2canvas(imageRef.current, {
        backgroundColor: '#f8fafc',
        scale,
        useCORS: true,
        logging: false,
        width: EXPORT_WIDTH,
        height: EXPORT_HEIGHT,
        windowWidth: EXPORT_WIDTH,
        windowHeight: EXPORT_HEIGHT,
        scrollX: 0,
        scrollY: 0,
      });

      const blob = await new Promise((resolve) => {
        canvas.toBlob(
          (blobResult) => resolve(blobResult),
          'image/jpeg',
          0.88
        );
      });

      if (!blob) {
        throw new Error('Failed to create image blob');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `markersync-palette-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating image:', error);
      await showConfirm({
        title: t('sharePaletteModal.error'),
        message: t('sharePaletteModal.imageError'),
        confirmText: 'OK',
        variant: 'warning',
        showCancel: false
      });
    } finally {
      setGeneratingImage(false);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      ref={overlayRef}
      onClick={onOverlayClick}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly"
      aria-modal="true"
      role="dialog"
    >
      <div className="relative w-full max-w-2xl animate-slideUp">
        <div className="card-elegant p-6 md:p-8 shadow-2xl border border-white/40 bg-white/90 backdrop-blur rounded-2xl">
          {/* <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
            aria-label={t("sharePaletteModal.closeAria")}
          >
            ✕
          </button>
          <div className="text-center mb-4">
            <h3 className="text-2xl font-display font-semibold text-slate-800 mb-2">
              {t("sharePaletteModal.title")}
            </h3>
            <p className="text-sm text-elegant">{t("sharePaletteModal.subtitle")}</p>
          </div> */}
          <div className="mb-4 lg:mb-6">
            <h3 className="text-lg lg:text-xl font-display font-semibold text-slate-800 mb-2">{t('sharePaletteModal.title')}</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full"></div>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9 text-slate-600 hover:text-slate-800 flex items-center justify-center"
            >
              ✕
            </button>
          </div>
          <div className="space-y-4">
            {/* Palette Code Section */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-3 border-b border-slate-200 bg-white/70 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">{t("sharePaletteModal.paletteCode")}</span>
                <button
                  onClick={() => copyToClipboard('code')}
                  className="px-3 py-2 text-xs font-medium rounded-xl bg-white/80 hover:bg-slate-50 border border-slate-200 text-slate-600 transition-all"
                  aria-live="polite"
                >
                  {copied && copyType === 'code' ? t("sharePaletteModal.copied") : t("sharePaletteModal.copy")}
                </button>
              </div>
              <div className="p-4">
                <code className="block font-mono text-sm md:text-base text-slate-800 break-all whitespace-pre-wrap">
                  {code}
                </code>
              </div>
            </div>

            {/* Share URL Section */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
              <div className="p-3 border-b border-blue-200 bg-white/70 flex items-center justify-between">
                <span className="text-sm font-semibold text-blue-700">{t("sharePaletteModal.urlCode")}</span>
                <button
                  onClick={() => copyToClipboard('url')}
                  className="px-3 py-2 text-xs font-medium rounded-xl bg-white/80 hover:bg-blue-50 border border-blue-200 text-blue-700 transition-all"
                  aria-live="polite"
                >
                  {copied && copyType === 'url' ? t("sharePaletteModal.copiedUrl") : t("sharePaletteModal.copyUrl")}
                </button>
              </div>
              <div className="p-4">
                <code className="block font-mono text-xs md:text-sm text-blue-800 break-all whitespace-pre-wrap cursor-pointer" onClick={() => window.open(generateShareUrl(), '_blank', 'noopener,noreferrer')}>
                  {generateShareUrl()}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
      {ConfirmModalComponent}
    </div>
  );
}
